import { supabase } from '../../config/supabase';

/**
 * Update a job/assignment
 * @param {string} jobId - The job ID (UUID or unique_id)
 * @param {Object} updateData - Data to update
 * @param {string} companyId - Company ID
 * @returns {Promise<Object>} Updated job
 */
export const updateAssignment = async (jobId, updateData, companyId) => {
    try {
        const { data, error } = await supabase
            .from('jobs')
            .update(updateData)
            .eq('id', jobId)
            .eq('company_id', companyId)
            .select()
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error updating assignment:', error);
        throw error;
    }
};

/**
 * Update job quotes
 * @param {string} jobId - The job ID
 * @param {Object} quotes - Object with quote types and their data
 * @returns {Promise<Array>} Updated quotes
 */
export const updateJobQuotes = async (jobId, quotes) => {
    try {
        const updates = [];

        for (const [quoteType, quoteData] of Object.entries(quotes)) {
            // Use upsert with onConflict to handle existing records
            const { data, error } = await supabase
                .from('job_quotes')
                .upsert(
                    {
                        job_id: jobId,
                        quote_type: quoteType,
                        is_required: quoteData.isRequired,
                        expected_delivery_date: quoteData.expectedDeliveryDate || null
                    },
                    {
                        onConflict: 'job_id,quote_type', // Specify the unique constraint columns
                        ignoreDuplicates: false // Update existing records
                    }
                )
                .select()
                .single();

            if (error) throw error;
            updates.push(data);
        }

        return updates;
    } catch (error) {
        console.error('Error updating job quotes:', error);
        throw error;
    }
};

/**
 * Update job floors
 * @param {string} jobId - The job ID
 * @param {Array} floors - Array of floor data
 * @returns {Promise<Array>} Updated floors
 */
export const updateJobFloors = async (jobId, floors) => {
    try {
        // First, delete existing floors
        const { error: deleteError } = await supabase
            .from('job_floors')
            .delete()
            .eq('job_id', jobId);

        if (deleteError) throw deleteError;

        // Then insert new floors
        const { data, error } = await supabase
            .from('job_floors')
            .insert(
                floors.map(floor => ({
                    job_id: jobId,
                    floor_number: floor.floorNumber,
                    floor_truss_size: floor.floorTrussSize || null,
                    flooring: floor.flooring || null,
                    balcony_included: floor.balconyIncluded
                }))
            )
            .select();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error updating job floors:', error);
        throw error;
    }
};

/**
 * Update job floor specs
 * @param {string} jobId - The job ID
 * @param {Object} floorSpecs - Floor specifications
 * @returns {Promise<Array>} Updated floor specs
 */
export const updateJobFloorSpecs = async (jobId, floorSpecs) => {
    try {
        const updates = [];

        // Delete existing specs first
        const { error: deleteError } = await supabase
            .from('job_floor_specs')
            .delete()
            .eq('job_id', jobId);

        if (deleteError) throw deleteError;

        // Insert new specs based on storey type
        if (floorSpecs.isSingleStorey) {
            const { data, error } = await supabase
                .from('job_floor_specs')
                .insert({
                    job_id: jobId,
                    floor_level: 'single_storey',
                    non_load_bearing_wall_stud_sizes: floorSpecs.singleStorey.nonLoadBearingWallStudSizes || null,
                    non_load_bearing_wall_stud_centres: floorSpecs.singleStorey.nonLoadBearingWallStudCentres || null,
                    wall_height: floorSpecs.singleStorey.wallHeight || null
                })
                .select()
                .single();

            if (error) throw error;
            updates.push(data);
        } else {
            // Ground floor
            const { data: groundData, error: groundError } = await supabase
                .from('job_floor_specs')
                .insert({
                    job_id: jobId,
                    floor_level: 'ground',
                    non_load_bearing_wall_stud_sizes: floorSpecs.doubleStorey.groundFloor.nonLoadBearingWallStudSizes || null,
                    non_load_bearing_wall_stud_centres: floorSpecs.doubleStorey.groundFloor.nonLoadBearingWallStudCentres || null,
                    wall_height: floorSpecs.doubleStorey.groundFloor.wallHeight || null
                })
                .select()
                .single();

            if (groundError) throw groundError;
            updates.push(groundData);

            // First floor
            const { data: firstData, error: firstError } = await supabase
                .from('job_floor_specs')
                .insert({
                    job_id: jobId,
                    floor_level: 'first',
                    non_load_bearing_wall_stud_sizes: floorSpecs.doubleStorey.firstFloor.nonLoadBearingWallStudSizes || null,
                    non_load_bearing_wall_stud_centres: floorSpecs.doubleStorey.firstFloor.nonLoadBearingWallStudCentres || null,
                    wall_height: floorSpecs.doubleStorey.firstFloor.wallHeight || null
                })
                .select()
                .single();

            if (firstError) throw firstError;
            updates.push(firstData);
        }

        return updates;
    } catch (error) {
        console.error('Error updating floor specs:', error);
        throw error;
    }
};

export const deleteJobFiles = async (fileIds) => {
    try {
        for (const fileId of fileIds) {
            // Get file info first
            const { data: fileData, error: fetchError } = await supabase
                .from('job_files')
                .select('filename')
                .eq('id', fileId)
                .single();

            if (fetchError) {
                console.error('Error fetching file:', fetchError);
                continue;
            }

            if (fileData && fileData.filename) {
                // Delete from job_files bucket (with underscore)
                const { error: storageError } = await supabase.storage
                    .from('job-files')
                    .remove([fileData.filename]);


                if (storageError) {
                    console.error('Error deleting from storage:', storageError);
                }

                // Delete from database
                const { error: dbError } = await supabase
                    .from('job_files')
                    .delete()
                    .eq('id', fileId);

                if (dbError) {
                    console.error('Error deleting from database:', dbError);
                }
            }
        }
    } catch (error) {
        console.error('Error deleting files:', error);
        throw error;
    }
};

/**
 * Log job action
 * @param {string} jobId - The job ID
 * @param {string} userId - User ID
 * @param {string} userName - User name
 * @param {string} userRole - User role
 * @param {string} action - Action description
 * @param {Object} details - Additional details
 * @returns {Promise<Object>} Created log entry
 */
export const logJobAction = async (jobId, userId, userName, userRole, action, details = {}) => {
    try {
        const { data, error } = await supabase
            .from('job_action_logs')
            .insert({
                job_id: jobId,
                user_id: userId,
                user_name: userName,
                user_role: userRole,
                action: action,
                details: typeof details === 'string' ? details : JSON.stringify(details)
            })
            .select()
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error logging action:', error);
        throw error;
    }
};